/**
 * 패널 라벨 인쇄 — A4 16칸(2×8, 105×37mm), 패널 한 장에 라벨 한 장
 * 라벨: 패널 번호(크게) · 구분 · 폭×길이 · 결합 · 재질 두께 · 견적번호 · 현장명 · QR(폰으로 찍으면 그 패널)
 */
import QRCode from 'qrcode';
import { PolyPanel } from '../engine/polyEngine';
import { CeilingPanel } from '../types';
import { numberPanels, ceilingNo, floorNo } from './panelNumbers';
import { jointLabel } from './productionSheet';

export interface LabelItem { no: string; kind: string; size: number; length: number; joint: string }

/** 도면 → 라벨 목록 (번호 순: 벽 → 칸막이 → 천장 → 바닥) */
export function labelItems(panels: PolyPanel[], ceiling: CeilingPanel[], floor: boolean, wallHeight: number): LabelItem[] {
  const nos = numberPanels(panels);
  const walls = panels.map(p => ({
    no: nos[p.id], kind: p.pieceKey.startsWith('part:') ? '칸막이' : '벽', size: p.size, length: wallHeight, joint: jointLabel(p.startType, p.endType),
  })).sort((a, b) => Number(a.kind === '칸막이') - Number(b.kind === '칸막이') || a.no.localeCompare(b.no, undefined, { numeric: true }));
  const ceil = (kind: string, no: (i: number) => string) => ceiling.map((c, i) => ({
    no: no(i), kind, size: c.size, length: c.length, joint: jointLabel(c.leftType as any, c.rightType as any),
  }));
  return [...walls, ...ceil('천장', ceilingNo), ...(floor ? ceil('바닥', floorNo) : [])];
}

const esc = (s: string) => s.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));

/** 패널 QR 주소 — 폰으로 찍으면 그 견적의 그 패널 */
export const panelUrl = (estiNo: string, no: string) => `${window.location.origin}/?p=${encodeURIComponent(`${estiNo}:${no}`)}`;

export async function printLabels(o: { items: LabelItem[]; estiNo: string; siteName: string; material: string; thick: number }) {
  const w = window.open('', '_blank'); // 클릭 즉시 열어야 팝업 차단 안 됨
  if (!w) { alert('팝업이 차단되었습니다. 브라우저에서 팝업을 허용해 주세요.'); return; }
  w.document.write('<p style="font-family:sans-serif">라벨 만드는 중…</p>');
  const qrs = await Promise.all(o.items.map(it => QRCode.toDataURL(panelUrl(o.estiNo, it.no), { margin: 0, width: 160, errorCorrectionLevel: 'M' })));
  const cells = o.items.map((it, i) => `
    <div class="lb">
      <img class="qr" src="${qrs[i]}">
      <div class="tx">
        <div class="no">${esc(it.no)}</div>
        <div class="sz">${it.kind} ${it.size} × ${it.length}</div>
        <div class="jt">${esc(it.joint)} · ${esc(o.material)} ${o.thick}T</div>
        <div class="ft">${esc(o.estiNo)} ${esc(o.siteName || '')}</div>
      </div>
    </div>`).join('');
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>패널 라벨 - ${esc(o.estiNo)}</title>
<style>
  @page { size: A4 portrait; margin: 0; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: "Malgun Gothic", Arial, sans-serif; color: #000; }
  .sheet { width: 210mm; display: grid; grid-template-columns: 105mm 105mm; grid-auto-rows: 37.125mm; }
  .lb { display: flex; align-items: center; gap: 3mm; padding: 2.5mm 4mm; overflow: hidden; border: 0.1mm dashed #ccc; break-inside: avoid; }
  .qr { width: 27mm; height: 27mm; flex: none; }
  .tx { min-width: 0; line-height: 1.25; }
  .no { font-size: 22pt; font-weight: 800; letter-spacing: 0.5px; }
  .sz { font-size: 11pt; font-weight: 700; }
  .jt { font-size: 9pt; }
  .ft { font-size: 8pt; color: #333; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  @media print { .lb { border-color: transparent; } .bar { display: none; } }
  .bar { padding: 8px; font-size: 13px; background: #FFF8E1; }
</style></head><body>
<div class="bar">총 ${o.items.length}장 — A4 16칸(2×8) 라벨지에 여백 없이 인쇄하세요. (점선은 화면에만 보임)</div>
<div class="sheet">${cells}</div>
<script>window.onload = () => setTimeout(() => window.print(), 300);</script>
</body></html>`;
  w.document.open(); w.document.write(html); w.document.close();
}
